/**
 * deposit
 * @returns ReactElement
 */
import { Button } from '@bfe/moly';
import { useEffect, useMemo, useState } from 'react';
import { useActivityStore } from '@/store/activity';
import { useFm, useToast } from '@web3/base-hooks';
import cls from 'classnames';
import {
  safeParseJSON,
  getCurrentLocale,
  removeFloatZero,
  toFormat,
  WalletChainTypes,
  WalletType,
  TON,
  SOLANA,
} from '@web3/base-utils';
import { PrepareTaskTypes } from '@/types';
import { TextClamp } from '@/components/TextClamp';
import { Page } from '@/config/track-config';
import { TokenBalance } from '@/types/user';
import { BybitWalletChain, WalletAddress } from '@/types/walletType';
import CopyText from '@/components/CopyText';
import { useWallet } from '@web3/base-hooks';
import { Web3DepositModal } from '@web3fe/infra';
import { useDeposite } from './hooks';
import { useBybitWallet } from '@/hooks/wallet';
import useStore from '@/store';
import { ACTIVITY_CHAIN_INFO } from '@/utils/constant';
import { bybitWalletAddChain } from '@/utils/appflyers';

import styles from './index.module.scss';

type DepositProp = {
  onShowSwitchWallet: () => void;
};

export function Deposit(props: DepositProp) {
  const activityChainInfo = useStore((state) => state.activityChainInfo);
  const { onShowSwitchWallet } = props;
  const [depositWalletAddress, setDepositWalletAddress] = useState('');
  const activityStore = useActivityStore();
  const {
    isLogin,
    evmWalletInfo,
    walletType,
    createWallet,
    openLoginModal,
    walletId,
    switchChain,
  } = useWallet();
  const { walletAddresses, initThirdAddresses } = useBybitWallet();
  const fm = useFm();
  const { toastWarn } = useToast();

  const { showDeposit, onChangeShowDeposite } = useDeposite();

  const prepareTaskNum = useMemo(
    () => activityStore.depositTaskList?.length || 0,
    [activityStore.depositTaskList]
  );

  const walletTypeList = useMemo(
    () => activityStore.activityDetail?.activityInfo?.walletTypeList || [],
    [activityStore.activityDetail]
  );
  const taskList = useMemo(
    () => activityStore.depositTaskList || [],
    [activityStore.depositTaskList]
  );

  // 充值任务config
  const taskRuleConfig = useMemo(() => {
    return {
      deposit: safeParseJSON(
        taskList.filter((item) => item.taskType === PrepareTaskTypes.deposit)[0]
          ?.taskRuleConfig,
        {} as any
      ),
      swap: safeParseJSON(
        taskList.filter((item) => item.taskType === PrepareTaskTypes.swap)[0]
          ?.taskRuleConfig,
        {} as any
      ),
    };
  }, [taskList]);

  const baseParams = useMemo(
    () => ({
      chainCode: taskRuleConfig?.deposit.depositChainCode,
      chainId: taskRuleConfig?.deposit.chainId,
      tokenAddress: taskRuleConfig?.deposit.depositContract,
    }),
    [taskRuleConfig]
  );
  const depositInit = async () => {
    if (!walletTypeList.includes(walletType)) {
      return;
    }
    if (!taskRuleConfig?.deposit?.depositChainCode) return;
    // 云钱包
    if (walletType === WalletType.CLOUD) {
      if (walletAddresses.length) {
        setDepositWalletAddress(
          walletAddresses.find(
            (item) => +item.chainId === +taskRuleConfig?.deposit.chainId
          )?.walletAddress
        );
      }
      activityStore.fetchDepositChainBalanceCloud({
        ...baseParams,
        walletId,
        walletType,
        walletAddress: walletAddresses.find(
          (item) => +item.chainId === +taskRuleConfig?.deposit.chainId
        )?.walletAddress,
        tokenAddress: taskRuleConfig?.deposit.depositContract,
      });
    } else {
      const chainListRes = await activityStore.fetchChainList();
      //   console.log('chainListRes===>', chainListRes);
      const currentChains =
        chainListRes?.walletChains['MNEMONIC_PHRASE']?.itemList?.filter(
          (item) => item.chainId == taskRuleConfig?.deposit?.chainId
        ) || [];
      // 判断当前任务配置中的chain的链类型是否跟当前连接钱包链类型一致
      if (!currentChains.length) return;

      const currentChainType: WalletChainTypes = currentChains[0].chainType;
      //插件钱包
      const addressChain = {
        ...baseParams,
        address: evmWalletInfo.address,
      };

      let bybitWalletInfo = {
        walletId: '',
        walletType: WalletType.CLOUD,
        addressChain: [] as BybitWalletChain[],
      };
      try {
        bybitWalletInfo =
          (await window.top?.bybitWallet?.request({
            method: 'getWalletInfo',
          })) || {};
        //充值任务配置的chain是否在当前钱包里
        const depositChainConfig = bybitWalletInfo.addressChain.filter(
          (chain) => chain.chainId == taskRuleConfig?.deposit.chainId
        );
        // 非EVM需要从钱包拿地址
        if (currentChainType !== WalletChainTypes.EVM) {
          addressChain.address = depositChainConfig.length
            ? depositChainConfig[0].address
            : '';
        }
      } catch (e) {
        console.log(e);
      }
      setDepositWalletAddress(addressChain.address);
      const params = {
        type: walletType,
        addressChain: [addressChain],
        walletId: bybitWalletInfo.walletId,
      };
      //有地址才查询余额
      addressChain.address && activityStore.fetchDepositChainBalanceEX(params);
    }
  };

  useEffect(() => {
    if (isLogin) {
      // 充值任务
      depositInit();
      // swap任务
      if (taskRuleConfig?.swap?.chainCode) {
        activityStore.fetchSwapChainBalanceCloud({
          ...baseParams,
          chainId: activityStore.activityDetail?.activityInfo?.chainId,
          walletId,
          walletType,
          walletAddress: walletAddresses.find(
            (item) =>
              +item.chainId ===
              activityStore.activityDetail?.activityInfo?.chainId
          )?.walletAddress,
        });
      }
    }
  }, [taskRuleConfig]);

  // 充值任务资产
  const balance = useMemo(() => {
    let depositAmount, swapAmount, deposit, swap;
    if (walletType === WalletType.CLOUD) {
      depositAmount = Array.isArray(activityStore.depositChainBalanceCloud)
        ? activityStore.depositChainBalanceCloud?.filter(
            (v) => v.symbol === taskRuleConfig.deposit?.depositSymbol
          )
        : [];
      swapAmount = Array.isArray(activityStore.swapChainBalance)
        ? activityStore.swapChainBalance?.filter(
            (v) => v.symbol === taskRuleConfig.swap?.tokenSymbol
          )
        : [];
      deposit = depositAmount[0]?.amount ?? 0;
      swap = swapAmount[0]?.amount ?? 0;
    } else {
      if (taskRuleConfig?.deposit?.depositContract) {
        depositAmount =
          activityStore.depositChainBalanceEX[0]?.tokenInfo.filter(
            (item) => item.contract === taskRuleConfig?.deposit?.depositContract
          )?.[0];
      } else {
        if (
          activityStore.activityDetail?.activityInfo?.chainId === TON.chainId
        ) {
          depositAmount =
            activityStore.depositChainBalanceEX[0]?.tokenInfo.filter(
              (item) => item.contract === TON.baseTokenContract
            )?.[0];
        } else if (
          activityStore.activityDetail?.activityInfo?.chainId === SOLANA.chainId
        ) {
          depositAmount =
            activityStore.depositChainBalanceEX[0]?.tokenInfo.filter(
              (item) => item.contract === SOLANA.baseTokenContract
            )?.[0];
        } else {
          const mainBalance =
            activityStore.depositChainBalanceEX[0]?.tokenInfo.filter(
              (item) => item.name === 'ETH' && item.contract === ''
            )?.[0];

          depositAmount =
            mainBalance ?? activityStore.depositChainBalanceEX[0]?.tokenInfo[0];
        }
      }

      swapAmount = activityStore.swapChainBalance as TokenBalance;
      deposit = (depositAmount as any)?.balance ?? 0;
      swap = swapAmount?.balance ?? 0;
    }
    return {
      deposit,
      swap,
    };
  }, [
    taskRuleConfig,
    activityStore.depositChainBalanceCloud,
    activityStore.depositChainBalanceEX,
    activityStore.swapChainBalance,
  ]);

  // 资产内容
  const BalanceComp = (props: any) => {
    const { data } = props;
    const isDepositTask = useMemo(
      () => data?.taskType === PrepareTaskTypes.deposit,
      [data]
    );
    const prepareToken = useMemo(() => {
      return isDepositTask
        ? taskRuleConfig?.deposit?.depositSymbol
        : taskRuleConfig?.swap?.tokenSymbol;
    }, [isDepositTask]);
    const prepareBalance = useMemo(() => {
      return toFormat(
        removeFloatZero(isDepositTask ? balance?.deposit : balance?.swap),
        2
      );
    }, [isDepositTask]);
    return (
      <div
        className={cls([
          'balance-wrap flex flex-row justify-between items-center gap-8',
          prepareTaskNum > 1 && 'justify-between items-center',
        ])}
      >
        <div
          className={cls([
            'flex flex-col',
            prepareTaskNum > 1 ? 'justify-end' : ' ',
          ])}
        >
          {prepareTaskNum > 1 && (
            <h4>
              {prepareToken} {fm('balance')}
            </h4>
          )}
          <div
            className={cls([
              'flex gap-2 items-center',
              prepareTaskNum > 1 ? '' : 'justify-end ',
            ])}
          >
            <span className="text-2xl font-semibold whitespace-nowrap">
              {isLogin ? prepareBalance : '--'}
            </span>
            <span className="text-lg">{prepareToken}</span>
          </div>
          {prepareTaskNum === 1 && depositWalletAddress && (
            <div className="flex justify-end">
              <CopyText text={depositWalletAddress} />{' '}
            </div>
          )}
        </div>
        <Button
          variant={'primary'}
          size="xs"
          className="min-w-36"
          data-spm-even="Web3UserClick"
          data-spm-data={JSON.stringify({
            page_name: Page.AirdropArcadeDetail,
            button_name: isDepositTask ? 'Deposit' : 'Swap',
          })}
          onClick={async () => {
            if (!isLogin) {
              openLoginModal();
            } else {
              if (
                !activityStore.activityDetail?.activityInfo.clientConfig
                  ?.pcConfig.support
              ) {
                toastWarn(fm('notSuppOnPc'));
                return;
              }
              if (evmWalletInfo.address) {
                if (!walletTypeList.includes(walletType)) {
                  onShowSwitchWallet();
                  return;
                }
                // 添加自定义链的逻辑
                if (data.chainId && data.chainId !== evmWalletInfo.chainId) {
                  if (!activityChainInfo) {
                    const taskChain = Object.values(ACTIVITY_CHAIN_INFO).find(
                      (item) =>
                        item.id ===
                        activityStore.activityDetail?.activityInfo.chainId
                    );
                    if (taskChain) {
                      await bybitWalletAddChain(taskChain.info);
                      initThirdAddresses();
                      return;
                    }
                  }
                  // 当前连接的钱包中不存在活动链
                  if (!activityChainInfo) {
                    toastWarn(fm('switchToSupportedNet'));
                    return;
                  }
                  // 非异构链才切链
                  if (
                    activityChainInfo &&
                    activityChainInfo.chainType === WalletChainTypes.EVM
                  ) {
                    switchChain(data.chainId);
                    return;
                  }
                }
                if (isDepositTask) {
                  onChangeShowDeposite(true);
                } else {
                  const url = `${window.top?.location.origin}/${getCurrentLocale()}/web3/swap-bridge#toChain=${taskRuleConfig.swap?.chainId || 1}&toToken=${taskRuleConfig?.swap.tokenContract}`;
                  window.open(url);
                }
              } else {
                if (
                  walletType === WalletType.CLOUD &&
                  walletTypeList.includes(WalletType.CLOUD)
                ) {
                  createWallet();
                } else {
                  onShowSwitchWallet();
                }
              }
            }
          }}
        >
          {isDepositTask ? fm('word_deposit') : fm('swap')}
        </Button>
      </div>
    );
  };
  return (
    <>
      {prepareTaskNum &&
      !(walletType === WalletType.PRIVATE_KEY && !depositWalletAddress) ? (
        <div className={`${styles['deposit-wrap']} relative main mt-12`}>
          <div className={cls(['flex justify-between', 'gap-8'])}>
            <div>
              <h2 className="text-2xl font-semibold">
                {prepareTaskNum > 1
                  ? fm('depositOrSwapToProceed')
                  : taskList[0]?.taskName}
              </h2>

              <div className="text-sm text-gray1 mt-2">
                {prepareTaskNum > 1 ? (
                  fm('requireTokensToContinueEvent', {
                    tokenName1: taskRuleConfig?.deposit.depositChainCode,
                    tokenName2: taskRuleConfig?.swap.chainCode,
                  })
                ) : (
                  <TextClamp line={2} text={taskList[0]?.taskDesc} />
                )}
              </div>
            </div>
            {prepareTaskNum === 1 && <BalanceComp data={taskList[0]} />}
          </div>
          {prepareTaskNum > 1 && (
            <div className="flex gap-8 mt-4">
              {taskList.map((item) => (
                <div className={styles['deposit-item']} key={item.configId}>
                  <BalanceComp data={item} />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
      <Web3DepositModal
        visible={showDeposit}
        token={{
          chainCode: taskRuleConfig?.deposit?.depositChainCode,
          token: taskRuleConfig?.deposit?.depositSymbol,
          walletAddress: depositWalletAddress,
        }}
        onClose={() => {
          onChangeShowDeposite(false);
        }}
      />
    </>
  );
}
